// components/MailSms/MailSmsManagementPage.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FiMail, FiMessageSquare } from 'react-icons/fi';
import axiosConfig from '../../../api/axiosConfig';

// Import sub-components
import TabNavigation from './components/TabNavigation';
import MessageAlert from './components/MessageAlert';
import SendTab from './components/SendTab';
import TemplatesTab from './components/TemplatesTab';
import SettingsTab from './components/SettingsTab';
import LogsTab from './components/LogsTab';
import TemplateModal from './components/TemplateModal';

const normalizeText = (value) => String(value ?? '').toLocaleLowerCase('tr-TR');

const RECIPIENT_PAGE_SIZE = 80;

const emptyPagination = {
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
  perPage: RECIPIENT_PAGE_SIZE,
  hasNext: false,
  hasPrev: false
};

const mergeById = (current, incoming) => {
  const byId = new Map(current.map(item => [String(item.id), item]));
  incoming.forEach(item => byId.set(String(item.id), item));
  return Array.from(byId.values());
};

const MailSmsManagementPage = () => {
  const [activeTab, setActiveTab] = useState('email');
  const [activeSubTab, setActiveSubTab] = useState('send');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Email States
  const [emailSettings, setEmailSettings] = useState({
    mail_host: 'smtp.gmail.com',
    mail_port: 587,
    mail_secure: false,
    mail_username: '',
    mail_password: '',
    mail_from_address: '',
    mail_from_name: ''
  });

  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailForm, setEmailForm] = useState({
    student_ids: [],
    template_id: '',
    custom_subject: '',
    custom_body: ''
  });

  // SMS States
  const [smsSettings, setSmsSettings] = useState({
    provider: 'netgsm',
    username: '',
    password: '',
    header: '',
    contact_phone: ''
  });

  const [smsTemplates, setSmsTemplates] = useState([]);
  const [smsForm, setSmsForm] = useState({
    student_ids: [],
    template_id: '',
    custom_message: ''
  });

  // Lesson SMS States
  const [lessons, setLessons] = useState([]);
  const [selectedLessons, setSelectedLessons] = useState([]);
  const [lessonSearchTerm, setLessonSearchTerm] = useState('');
  const [lessonPagination, setLessonPagination] = useState(emptyPagination);

  // Students & Logs
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [smsLogs, setSmsLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [studentPagination, setStudentPagination] = useState(emptyPagination);

  // Template Modal States
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateModalType, setTemplateModalType] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    body: '',
    content: ''
  });

  // SMS Send Type (student or lesson)
  const [smsSendType, setSmsSendType] = useState('student');

  // Ders grupları ve öğretmenler için state'ler
  const [lessonGroups, setLessonGroups] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const initialDataRequestRef = useRef(false);
  const requestSeqRef = useRef({
    students: 0,
    lessons: 0
  });
  const requestStatusRef = useRef({
    students: false,
    lessons: false,
    emailSettings: false,
    smsSettings: false,
    emailTemplates: false,
    smsTemplates: false
  });
  const loadedDataRef = useRef({
    students: false,
    lessons: false,
    emailSettings: false,
    smsSettings: false,
    emailTemplates: false,
    smsTemplates: false
  });

  // Loading states for different data
  const [dataLoading, setDataLoading] = useState({
    students: false,
    lessons: false,
    studentsMore: false,
    lessonsMore: false,
    settings: false,
    templates: false
  });

  // Initial data fetch
  useEffect(() => {
    if (initialDataRequestRef.current) return;
    initialDataRequestRef.current = true;

    const loadInitialData = async () => {
      try {
        const [lessonGroupsRes, teachersRes] = await Promise.all([
          axiosConfig.get('/admin/dersgrubu'),
          axiosConfig.get('/admin/teachers')
        ]);
        setLessonGroups(Array.isArray(lessonGroupsRes.data) ? lessonGroupsRes.data : []);
        setTeachers(Array.isArray(teachersRes.data) ? teachersRes.data : []);
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setInitialDataLoaded(true);
      }
    };
    loadInitialData();
  }, []);

  // Tab değişiminde veri yükleme
  // API Calls
  // eslint-disable-next-line no-unused-vars
  const fetchStudents = useCallback(async () => {
    if (requestStatusRef.current.students) return;
    requestStatusRef.current.students = true;
    
    setDataLoading(prev => ({ ...prev, students: true }));
    try {
      const [studentsResponse, kursSatisResponse] = await Promise.all([
        axiosConfig.get('/admin/ogrencis'),
        axiosConfig.get('/admin/kurssatis')
      ]);
      
      // Yeni pagination formatı desteği
      let studentsData = [];
      if (studentsResponse.data) {
        if (Array.isArray(studentsResponse.data)) {
          // Eski format
          studentsData = studentsResponse.data;
        } else if (studentsResponse.data.data && Array.isArray(studentsResponse.data.data)) {
          // Yeni pagination formatı
          studentsData = studentsResponse.data.data;
        }
      }
      
      // KursSatis verilerini al
      let kursSatisData = [];
      if (kursSatisResponse.data) {
        if (Array.isArray(kursSatisResponse.data)) {
          kursSatisData = kursSatisResponse.data;
        } else if (kursSatisResponse.data.data && Array.isArray(kursSatisResponse.data.data)) {
          kursSatisData = kursSatisResponse.data.data;
        }
      }
      const kursSatisByStudentId = new Map(kursSatisData.map(ks => [String(ks.musteri_id), ks]));
      const teachersById = new Map(teachers.map(teacher => [String(teacher.id), teacher]));
      
      // Öğrencilere öğretmen bilgisi ekle
      const studentsWithTeacher = studentsData.map(student => {
        const kursSatis = kursSatisByStudentId.get(String(student.id));
        const rawTeacherIds = kursSatis?.ogretmenler;
        let teacherIds = [];

        if (Array.isArray(rawTeacherIds)) {
          teacherIds = rawTeacherIds;
        } else if (typeof rawTeacherIds === 'string' && rawTeacherIds.trim() !== '') {
          try {
            const parsed = JSON.parse(rawTeacherIds);
            teacherIds = Array.isArray(parsed) ? parsed : [];
          } catch (e) {
            teacherIds = [];
          }
        }

        const teacherNames = teacherIds
          .map(tId => {
            const teacher = teachersById.get(String(tId));
            return teacher?.adi || '';
          })
          .filter(name => name);

        return {
          ...student,
          ogretmenler: teacherNames.join(', ') || 'Atanmamış'
        };
      });
      
      console.log('Fetched students:', studentsWithTeacher.length); // Debug için
      setStudents(studentsWithTeacher);
      loadedDataRef.current.students = true;
    } catch (error) {
      console.error('Error fetching students:', error);
      setStudents([]);
    } finally {
      requestStatusRef.current.students = false;
      setDataLoading(prev => ({ ...prev, students: false }));
    }
  }, [teachers]);

  const fetchLessons = useCallback(async () => {
    if (requestStatusRef.current.lessons || lessonGroups.length === 0) return;
    requestStatusRef.current.lessons = true;
    
    setDataLoading(prev => ({ ...prev, lessons: true }));
    try {
      const [lessonsRes, studentsRes] = await Promise.all([
        axiosConfig.get('/admin/ogretmenders'),
        axiosConfig.get('/admin/ogrencis') // Her zaman öğrencileri de çek
      ]);
      
      const allLessons = Array.isArray(lessonsRes.data?.data) ? lessonsRes.data.data : [];
      
      // Öğrenci verilerini kontrol et
      let studentsData = [];
      if (studentsRes.data) {
        if (Array.isArray(studentsRes.data)) {
          studentsData = studentsRes.data;
        } else if (studentsRes.data.data && Array.isArray(studentsRes.data.data)) {
          studentsData = studentsRes.data.data;
        }
      }
      
      // Students state'ini güncelle
      if (studentsData.length > 0) {
        setStudents(studentsData);
      }
      const studentsById = new Map(studentsData.map(student => [String(student.id), student]));
      const teachersById = new Map(teachers.map(teacher => [String(teacher.id), teacher]));
      const lessonGroupsById = new Map(lessonGroups.map(group => [String(group.id), group]));
      
      const lessonsWithStudentInfo = allLessons
        .map(lesson => {
          const student = studentsById.get(String(lesson.ogrenci_id));
          const teacher = teachersById.get(String(lesson.ogretmen_id));
          return {
            ...lesson,
            ogrenci_adi: student?.name || 'Bilinmeyen Öğrenci',
            ogretmen_adi: teacher?.adi || 'Bilinmeyen Öğretmen',
            parent_phone: student?.parent_phone || '',
            kalan_bakiye: student?.kalan_bakiye || 0,
            ders_adi: lessonGroupsById.get(String(lesson.ders_id))?.ders_adi || 'Ders'
          };
        })
        .filter(lesson => lesson.parent_phone);
      
      console.log('Fetched lessons:', lessonsWithStudentInfo.length); // Debug için
      setLessons(lessonsWithStudentInfo);
      loadedDataRef.current.lessons = true;
    } catch (error) {
      console.error('Error fetching lessons:', error);
      setLessons([]);
    } finally {
      requestStatusRef.current.lessons = false;
      setDataLoading(prev => ({ ...prev, lessons: false }));
    }
  }, [lessonGroups, teachers]);

  const fetchPagedStudents = useCallback(async ({ page = 1, append = false, search = searchTerm } = {}) => {
    const requestId = ++requestSeqRef.current.students;
    const loadingKey = append ? 'studentsMore' : 'students';

    setDataLoading(prev => ({ ...prev, [loadingKey]: true }));
    try {
      const response = await axiosConfig.get('/admin/email-sms/recipients/students', {
        params: {
          page,
          limit: RECIPIENT_PAGE_SIZE,
          search: search.trim()
        }
      });

      if (requestId !== requestSeqRef.current.students) return;

      const rows = Array.isArray(response.data?.data) ? response.data.data : [];
      setStudents(prev => append ? mergeById(prev, rows) : rows);
      setStudentPagination(response.data?.pagination || emptyPagination);
      loadedDataRef.current.students = true;
    } catch (error) {
      if (requestId === requestSeqRef.current.students) {
        console.error('Error fetching paged students:', error);
        if (!append) setStudents([]);
      }
    } finally {
      if (requestId === requestSeqRef.current.students) {
        setDataLoading(prev => ({ ...prev, [loadingKey]: false }));
      }
    }
  }, [searchTerm]);

  const fetchPagedLessons = useCallback(async ({ page = 1, append = false, search = lessonSearchTerm } = {}) => {
    const requestId = ++requestSeqRef.current.lessons;
    const loadingKey = append ? 'lessonsMore' : 'lessons';

    setDataLoading(prev => ({ ...prev, [loadingKey]: true }));
    try {
      const response = await axiosConfig.get('/admin/email-sms/recipients/lessons', {
        params: {
          page,
          limit: RECIPIENT_PAGE_SIZE,
          search: search.trim()
        }
      });

      if (requestId !== requestSeqRef.current.lessons) return;

      const rows = Array.isArray(response.data?.data) ? response.data.data : [];
      setLessons(prev => append ? mergeById(prev, rows) : rows);
      setLessonPagination(response.data?.pagination || emptyPagination);
      loadedDataRef.current.lessons = true;
    } catch (error) {
      if (requestId === requestSeqRef.current.lessons) {
        console.error('Error fetching paged lessons:', error);
        if (!append) setLessons([]);
      }
    } finally {
      if (requestId === requestSeqRef.current.lessons) {
        setDataLoading(prev => ({ ...prev, [loadingKey]: false }));
      }
    }
  }, [lessonSearchTerm]);

  const loadMoreStudents = useCallback(() => {
    if (!studentPagination.hasNext || dataLoading.studentsMore) return;
    fetchPagedStudents({
      page: studentPagination.currentPage + 1,
      append: true,
      search: searchTerm
    });
  }, [dataLoading.studentsMore, fetchPagedStudents, searchTerm, studentPagination]);

  const loadMoreLessons = useCallback(() => {
    if (!lessonPagination.hasNext || dataLoading.lessonsMore) return;
    fetchPagedLessons({
      page: lessonPagination.currentPage + 1,
      append: true,
      search: lessonSearchTerm
    });
  }, [dataLoading.lessonsMore, fetchPagedLessons, lessonPagination, lessonSearchTerm]);

  const fetchEmailSettings = useCallback(async () => {
    if (requestStatusRef.current.emailSettings) return;
    requestStatusRef.current.emailSettings = true;
    
    setDataLoading(prev => ({ ...prev, settings: true }));
    try {
      const response = await axiosConfig.get('/admin/email-sms/settings/email');
      if (response.data) setEmailSettings(response.data);
      loadedDataRef.current.emailSettings = true;
    } catch (error) {
      console.error('Error fetching email settings:', error);
    } finally {
      requestStatusRef.current.emailSettings = false;
      setDataLoading(prev => ({ ...prev, settings: false }));
    }
  }, []);

  const fetchSmsSettings = useCallback(async () => {
    if (requestStatusRef.current.smsSettings) return;
    requestStatusRef.current.smsSettings = true;
    
    setDataLoading(prev => ({ ...prev, settings: true }));
    try {
      const response = await axiosConfig.get('/admin/email-sms/settings/sms');
      if (response.data) setSmsSettings(response.data);
      loadedDataRef.current.smsSettings = true;
    } catch (error) {
      console.error('Error fetching SMS settings:', error);
    } finally {
      requestStatusRef.current.smsSettings = false;
      setDataLoading(prev => ({ ...prev, settings: false }));
    }
  }, []);

  const fetchEmailTemplates = useCallback(async () => {
    if (requestStatusRef.current.emailTemplates) return;
    requestStatusRef.current.emailTemplates = true;
    
    setDataLoading(prev => ({ ...prev, templates: true }));
    try {
      const response = await axiosConfig.get('/admin/email-sms/templates/email');
      setEmailTemplates(Array.isArray(response.data) ? response.data : []);
      loadedDataRef.current.emailTemplates = true;
    } catch (error) {
      console.error('Error fetching email templates:', error);
    } finally {
      requestStatusRef.current.emailTemplates = false;
      setDataLoading(prev => ({ ...prev, templates: false }));
    }
  }, []);

  const fetchSmsTemplates = useCallback(async () => {
    if (requestStatusRef.current.smsTemplates) return;
    requestStatusRef.current.smsTemplates = true;
    
    setDataLoading(prev => ({ ...prev, templates: true }));
    try {
      const response = await axiosConfig.get('/admin/email-sms/templates/sms');
      setSmsTemplates(Array.isArray(response.data) ? response.data : []);
      loadedDataRef.current.smsTemplates = true;
    } catch (error) {
      console.error('Error fetching SMS templates:', error);
    } finally {
      requestStatusRef.current.smsTemplates = false;
      setDataLoading(prev => ({ ...prev, templates: false }));
    }
  }, []);

  const fetchEmailLogs = useCallback(async () => {
    try {
      const response = await axiosConfig.get('/admin/email-sms/logs/email');
      setEmailLogs(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching email logs:', error);
    }
  }, []);

  const fetchSmsLogs = useCallback(async () => {
    try {
      const response = await axiosConfig.get('/admin/email-sms/logs/sms');
      setSmsLogs(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching SMS logs:', error);
    }
  }, []);

  useEffect(() => {
    if (!initialDataLoaded) return;

    if (activeTab === 'email') {
      if (activeSubTab === 'send') {
        if (!loadedDataRef.current.students) fetchPagedStudents({ page: 1, search: searchTerm });
        if (!loadedDataRef.current.emailTemplates) fetchEmailTemplates();
      }
      if (activeSubTab === 'settings' && !loadedDataRef.current.emailSettings && !emailSettings.mail_username) fetchEmailSettings();
      if (activeSubTab === 'templates' && !loadedDataRef.current.emailTemplates) fetchEmailTemplates();
      if (activeSubTab === 'logs') fetchEmailLogs();
    } else {
      if (activeSubTab === 'send') {
        if (smsSendType === 'student') {
          if (!loadedDataRef.current.students) fetchPagedStudents({ page: 1, search: searchTerm });
          if (!loadedDataRef.current.smsTemplates) fetchSmsTemplates();
        } else if (smsSendType === 'lesson') {
          if (!loadedDataRef.current.lessons) fetchPagedLessons({ page: 1, search: lessonSearchTerm });
        }
      }
      if (activeSubTab === 'settings' && !loadedDataRef.current.smsSettings && !smsSettings.username) fetchSmsSettings();
      if (activeSubTab === 'templates' && !loadedDataRef.current.smsTemplates) fetchSmsTemplates();
      if (activeSubTab === 'logs') fetchSmsLogs();
    }
  }, [
    activeTab,
    activeSubTab,
    smsSendType,
    initialDataLoaded,
    emailSettings.mail_username,
    smsSettings.username,
    fetchPagedStudents,
    fetchEmailTemplates,
    fetchEmailSettings,
    fetchEmailLogs,
    fetchSmsTemplates,
    fetchSmsSettings,
    fetchSmsLogs,
    fetchPagedLessons,
    searchTerm,
    lessonSearchTerm
  ]);

  useEffect(() => {
    if (!initialDataLoaded || activeSubTab !== 'send') return;
    if (!(activeTab === 'email' || (activeTab === 'sms' && smsSendType === 'student'))) return;
    if (!loadedDataRef.current.students && !searchTerm) return;

    const timer = setTimeout(() => {
      fetchPagedStudents({ page: 1, search: searchTerm });
    }, 350);

    return () => clearTimeout(timer);
  }, [activeTab, activeSubTab, smsSendType, initialDataLoaded, searchTerm, fetchPagedStudents]);

  useEffect(() => {
    if (!initialDataLoaded || activeSubTab !== 'send') return;
    if (!(activeTab === 'sms' && smsSendType === 'lesson')) return;
    if (!loadedDataRef.current.lessons && !lessonSearchTerm) return;

    const timer = setTimeout(() => {
      fetchPagedLessons({ page: 1, search: lessonSearchTerm });
    }, 350);

    return () => clearTimeout(timer);
  }, [activeTab, activeSubTab, smsSendType, initialDataLoaded, lessonSearchTerm, fetchPagedLessons]);

  // Handlers
  const handleSaveEmailSettings = async () => {
    setLoading(true);
    try {
      await axiosConfig.post('/admin/email-sms/settings/email', emailSettings);
      setMessage({ type: 'success', text: 'Email ayarları başarıyla kaydedildi!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Email ayarları kaydedilemedi!' });
    }
    setLoading(false);
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleSaveSmsSettings = async () => {
    setLoading(true);
    try {
      await axiosConfig.post('/admin/email-sms/settings/sms', smsSettings);
      setMessage({ type: 'success', text: 'SMS ayarları başarıyla kaydedildi!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'SMS ayarları kaydedilemedi!' });
    }
    setLoading(false);
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  // Kuyruğa alınan toplu gönderimin ilerlemesini periyodik olarak sorgular
  const pollBatch = useCallback((batchId, label) => {
    if (!batchId) return;
    let attempts = 0;
    const maxAttempts = 90; // ~3 dk (2sn aralık)
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axiosConfig.get(`/admin/email-sms/jobs/${batchId}`);
        const sent = data.sent || 0;
        const failed = data.failed || 0;
        const total = data.total || 0;
        if (data.done) {
          setMessage({
            type: failed > 0 ? 'error' : 'success',
            text: `${label} tamamlandı — Başarılı: ${sent}${failed ? `, Başarısız: ${failed}` : ''} (Toplam: ${total})`
          });
          return;
        }
        setMessage({ type: 'success', text: `${label}: ${sent + failed}/${total} işlendi…` });
      } catch (e) {
        // sessizce yut; sonraki denemede tekrar dener
      }
      if (attempts < maxAttempts) setTimeout(poll, 2000);
    };
    setTimeout(poll, 1500);
  }, []);

  const handleSendEmail = async () => {
    if (selectedStudents.length === 0) {
      setMessage({ type: 'error', text: 'Lütfen en az bir öğrenci seçin!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    setLoading(true);
    try {
      const response = await axiosConfig.post('/admin/email-sms/send/email/bulk', {
        ...emailForm,
        student_ids: selectedStudents
      });
      setMessage({ type: 'success', text: response?.data?.message || 'E-postalar kuyruğa alındı.' });
      setSelectedStudents([]);
      setEmailForm({ ...emailForm, template_id: '', custom_subject: '', custom_body: '' });
      pollBatch(response?.data?.data?.batch_id, 'E-posta gönderimi');
    } catch (error) {
      setMessage({ type: 'error', text: 'E-posta kuyruğa eklenemedi!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
    setLoading(false);
  };

  const handleSendSms = async () => {
    if (smsSendType === 'student') {
      if (selectedStudents.length === 0) {
        setMessage({ type: 'error', text: 'Lütfen en az bir öğrenci seçin!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        return;
      }

      setLoading(true);
      try {
        const response = await axiosConfig.post('/admin/email-sms/send/sms/bulk', {
          ...smsForm,
          student_ids: selectedStudents
        });
        setMessage({ type: 'success', text: response?.data?.message || 'SMS\'ler kuyruğa alındı.' });
        setSelectedStudents([]);
        setSmsForm({ ...smsForm, template_id: '', custom_message: '' });
        pollBatch(response?.data?.data?.batch_id, 'SMS gönderimi');
      } catch (error) {
        setMessage({ type: 'error', text: 'SMS kuyruğa eklenemedi!' });
      }
      setLoading(false);
    } else {
      if (selectedLessons.length === 0) {
        setMessage({ type: 'error', text: 'Lütfen en az bir ders seçin!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        return;
      }

      setLoading(true);
      try {
        if (Array.isArray(selectedLessons)) {
          const response = await axiosConfig.post('/admin/email-sms/send/sms/lesson-notification', {
            lesson_ids: selectedLessons
          });

          setMessage({ type: 'success', text: response?.data?.message || 'Ders bilgilendirme SMS\'leri kuyruÄŸa alÄ±ndÄ±.' });
          setSelectedLessons([]);
          pollBatch(response?.data?.data?.batch_id, 'Ders SMS gÃ¶nderimi');
          await fetchPagedLessons({ page: 1, search: lessonSearchTerm });
          setLoading(false);
          return;
        }

        const lessonData = selectedLessons
          .map(lessonId => {
            const lesson = lessons.find(l => String(l.id) === String(lessonId));
            if (!lesson) return null;
            return {
              ders_id: lesson.id,
              ogrenci_id: lesson.ogrenci_id,
              ogrenci_ad: lesson.ogrenci_adi,
              veli_telefon: lesson.parent_phone,
              baslangic_tarihi: new Date(lesson.baslangic_tarihi).toLocaleDateString('tr-TR'),
              ders: lesson.ders_adi || 'Ders',
              ders_sure: lesson.ders_sure,
              ders_konusu: lesson.ders_konusu,
              kalan_bakiye: lesson.kalan_bakiye
            };
          })
          .filter(Boolean);

        if (lessonData.length === 0) {
          setMessage({ type: 'error', text: 'Seçilen ders bilgileri bulunamadı.' });
          setLoading(false);
          setTimeout(() => setMessage({ type: '', text: '' }), 3000);
          return;
        }

        const response = await axiosConfig.post('/admin/email-sms/send/sms/lesson-notification', {
          lesson_data: lessonData
        });
        
        setMessage({ type: 'success', text: response?.data?.message || 'Ders bilgilendirme SMS\'leri kuyruğa alındı.' });
        setSelectedLessons([]);
        pollBatch(response?.data?.data?.batch_id, 'Ders SMS gönderimi');
        await fetchLessons();
      } catch (error) {
        setMessage({ type: 'error', text: 'SMS gönderimi başarısız!' });
      }
      setLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    setLoading(true);
    try {
      const endpoint = templateModalType === 'email' ? '/admin/email-sms/templates/email' : '/admin/email-sms/templates/sms';
      const payload = templateModalType === 'email'
        ? {
            name: templateForm.name,
            subject: templateForm.subject,
            body: templateForm.body
          }
        : {
            name: templateForm.name,
            content: templateForm.content
          };
      const isEditMode = Boolean(editingTemplate && editingTemplate.type === templateModalType);

      if (isEditMode) {
        await axiosConfig.put(`${endpoint}/${editingTemplate.id}`, payload);
      } else {
        await axiosConfig.post(endpoint, payload);
      }
      setMessage({ type: 'success', text: isEditMode ? 'Şablon başarıyla güncellendi!' : 'Şablon başarıyla kaydedildi!' });
      setShowTemplateModal(false);
      setEditingTemplate(null);
      setTemplateForm({ name: '', subject: '', body: '', content: '' });
      templateModalType === 'email' ? fetchEmailTemplates() : fetchSmsTemplates();
    } catch (error) {
      setMessage({ type: 'error', text: 'Şablon kaydedilemedi!' });
    }
    setLoading(false);
  };

  const handleDeleteTemplate = async (id, type) => {
    if (!window.confirm('Bu şablonu silmek istediğinize emin misiniz?')) return;
    
    try {
      const endpoint = type === 'email' ? `/admin/email-sms/templates/email/${id}` : `/admin/email-sms/templates/sms/${id}`;
      await axiosConfig.delete(endpoint);
      setMessage({ type: 'success', text: 'Şablon silindi!' });
      type === 'email' ? fetchEmailTemplates() : fetchSmsTemplates();
    } catch (error) {
      setMessage({ type: 'error', text: 'Şablon silinemedi!' });
    }
  };

  // Memoized filtered data
  const filteredStudents = useMemo(() => {
    if (!Array.isArray(students) || students.length === 0) {
      console.log('No students to filter'); // Debug için
      return [];
    }
    return students.filter(student => {
      const searchLower = normalizeText(searchTerm);
      return (
        normalizeText(student.name).includes(searchLower) ||
        normalizeText(student.email).includes(searchLower) ||
        normalizeText(student.parent_phone).includes(searchLower)
      );
    });
  }, [students, searchTerm]);

  const filteredLessons = useMemo(() => {
    if (!Array.isArray(lessons)) return [];
    return lessons.filter(lesson => {
      const searchLower = normalizeText(lessonSearchTerm);
      return (
        normalizeText(lesson.ogrenci_adi).includes(searchLower) ||
        normalizeText(lesson.ders_adi).includes(searchLower) ||
        normalizeText(lesson.ders_konusu).includes(searchLower)
      );
    });
  }, [lessons, lessonSearchTerm]);

  const mainTabs = [
    { id: 'email', label: 'Email Yönetimi', icon: FiMail },
    { id: 'sms', label: 'SMS Yönetimi', icon: FiMessageSquare }
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Mail & SMS Yönetimi</h1>
        <p className="text-gray-600">Toplu mail ve SMS gönderimi yapın, şablonları yönetin</p>
      </div>

      {/* Message Alert */}
      {message.text && <MessageAlert message={message} />}

      {/* Main Container */}
      <div className="bg-white rounded-xl shadow-lg">
        {/* Main Tabs */}
        <TabNavigation 
          tabs={mainTabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Sub Tabs */}
        <TabNavigation 
          tabs={[
            { id: 'send', label: 'Gönder', icon: 'FiSend' },
            { id: 'templates', label: 'Şablonlar', icon: 'FiFileText' },
            { id: 'settings', label: 'Ayarlar', icon: 'FiSettings' },
            { id: 'logs', label: 'Geçmiş', icon: 'FiClock' }
          ]}
          activeTab={activeSubTab}
          onTabChange={setActiveSubTab}
          isSubTab
        />

        {/* Content */}
        <div className="p-6">
          {activeSubTab === 'send' && (
            <SendTab
              activeTab={activeTab}
              smsSendType={smsSendType}
              setSmsSendType={setSmsSendType}
              students={students}
              lessons={lessons}
              selectedStudents={selectedStudents}
              setSelectedStudents={setSelectedStudents}
              selectedLessons={selectedLessons}
              setSelectedLessons={setSelectedLessons}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              lessonSearchTerm={lessonSearchTerm}
              setLessonSearchTerm={setLessonSearchTerm}
              filteredStudents={filteredStudents}
              filteredLessons={filteredLessons}
              studentPagination={studentPagination}
              lessonPagination={lessonPagination}
              loadMoreStudents={loadMoreStudents}
              loadMoreLessons={loadMoreLessons}
              emailForm={emailForm}
              setEmailForm={setEmailForm}
              smsForm={smsForm}
              setSmsForm={setSmsForm}
              emailTemplates={emailTemplates}
              smsTemplates={smsTemplates}
              smsSettings={smsSettings}
              handleSendEmail={handleSendEmail}
              handleSendSms={handleSendSms}
              loading={loading}
              dataLoading={dataLoading}
            />
          )}

          {activeSubTab === 'templates' && (
            <TemplatesTab
              activeTab={activeTab}
              emailTemplates={emailTemplates}
              smsTemplates={smsTemplates}
              setEmailForm={setEmailForm}
              setSmsForm={setSmsForm}
              setTemplateForm={setTemplateForm}
              setActiveSubTab={setActiveSubTab}
              handleDeleteTemplate={handleDeleteTemplate}
              setTemplateModalType={setTemplateModalType}
              setEditingTemplate={setEditingTemplate}
              setShowTemplateModal={setShowTemplateModal}
              dataLoading={dataLoading}
            />
          )}

          {activeSubTab === 'settings' && (
            <SettingsTab
              activeTab={activeTab}
              emailSettings={emailSettings}
              setEmailSettings={setEmailSettings}
              smsSettings={smsSettings}
              setSmsSettings={setSmsSettings}
              handleSaveEmailSettings={handleSaveEmailSettings}
              handleSaveSmsSettings={handleSaveSmsSettings}
              loading={loading}
              dataLoading={dataLoading}
            />
          )}

          {activeSubTab === 'logs' && (
            <LogsTab
              activeTab={activeTab}
              emailLogs={emailLogs}
              smsLogs={smsLogs}
            />
          )}
          
          {/* Debug bilgisi - production'da kaldırın */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-4 p-4 bg-gray-100 rounded text-xs text-gray-600">
              Debug: Students: {students.length}, Lessons: {lessons.length}, 
              Loading: {JSON.stringify(dataLoading)}
            </div>
          )}
        </div>
      </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <TemplateModal
          showModal={showTemplateModal}
          setShowModal={setShowTemplateModal}
          templateModalType={templateModalType}
          editingTemplate={editingTemplate}
          setEditingTemplate={setEditingTemplate}
          templateForm={templateForm}
          setTemplateForm={setTemplateForm}
          handleSaveTemplate={handleSaveTemplate}
          loading={loading}
        />
      )}
    </div>
  );
};

export default MailSmsManagementPage;
